// ** React Imports
import { useEffect, useState } from 'react'

import { client } from 'src/utils/axios-client';
import { LeadScreenStore, LeadActivity, Lead, NextActivity, LeadActivityStore, ViewType, LeadAccountStore, LeadCompanyStore, SprintDialogStore, LeadSprintStore, LeadActivityHistoryStore, HistoryDetailsDialogStore, ActivityHistoryDialogStore, DiscoveryCallAttendeesStore } from 'src/stores/LeadStore';
import { leadEndpoints } from '../../../configs/endpoints';

// ** MUI Imports
import Typography from '@mui/material/Typography'
import CardContent from '@mui/material/CardContent'
import Tooltip from '@mui/material/Tooltip'
import QuestionAnswerTwoToneIcon from '@mui/icons-material/QuestionAnswerTwoTone';
import _ from 'lodash';
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import MuiTimeline, { TimelineProps } from '@mui/lab/Timeline'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineItem from '@mui/lab/TimelineItem'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import { styled } from '@mui/material/styles'
import Icon from 'src/features/common/CustomIcon'
import { previousDateDays } from 'src/utils/date-utils';
import Link from '@mui/material/Link';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { ProblemDetails } from '../../../types/ApiErrorResponse';

// Styled Timeline component
const Timeline = styled(MuiTimeline)<TimelineProps>(({ theme }) => ({
    margin: 0,
    padding: 0,
    marginLeft: theme.spacing(0.75),
    '& .MuiTimelineItem-root': {
        '&:before': {
            display: 'none'
        },
        '&:last-child': {
            minHeight: 60
        }
    }
}))

type Props = {
    lead: Lead
}



const DiscoveryCallAttendees = ({ lead }: Props) => {
    const discoveryCallAttendeesRecord = DiscoveryCallAttendeesStore.useState(s => s.items);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [addAttendee, setAddAttendee] = useState(false);
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [attendeeAdded, setAttendeeAdded] = useState(false);

    useEffect(() => {
        const fetchAttendees = async () => {
            setHasLoaded(false);
            const response = await client.get(leadEndpoints.getdiscoverycallattendees, { params: { leadId: lead.id } });
            DiscoveryCallAttendeesStore.update(s => { s.items = response.data });
            setHasLoaded(true);
            
            
        }

        if (lead) fetchAttendees();
        
    }, [lead, attendeeAdded])

    const onDeleteAttendee = async (id: string) => {
        await client.post(leadEndpoints.deleteattendee, { id: id })
            .then(async (response) => {
                
                setAttendeeAdded(attendeeAdded ? false : true);
          
            }).catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        var errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\'
                        });
                    }
                }
            });
    };

    const onAddAttendee = () => {
        setAddAttendee(true);
        setAttendeeAdded(false);
    };

    const handleFullNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFullName(event.target.value)
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value)
    };

    const handleJobTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setJobTitle(event.target.value)
    };

    const onCancelSaveAttendee = () => {
        setFullName('');
        setEmail('');
        setJobTitle('');
        setAddAttendee(false);
    };

    const onSaveAttendee = async () => {
        if (fullName == "") return;
        
        await client.post(leadEndpoints.createnewattendee, { leadId: lead.id , fullName: fullName, email: email, jobTitle: jobTitle} )
            .then(async (response) => {

                setFullName('');
                setEmail('');
                setJobTitle('');
                setAttendeeAdded(true);
                setAddAttendee(false);

            }).catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        var errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\'
                        });
                    }
                }
            });
    }

    

    if (hasLoaded) {
        return <Card sx={{ mb: 6 }} data-testid="activity-timeline">
            <CardContent>
                <Grid container spacing={2} justifyContent={'right'}>
                    <Grid item xs={12} md={8} justifyContent="left" sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Typography sx={{ fontSize: 18}}>
                            Attendees
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} justifyContent="center" >
                        <Tooltip title="Add Attendee" >
                            <IconButton onClick={onAddAttendee} color="info">
                                <PersonAddAltIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                {addAttendee &&
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={12} justifyContent="left">
                            <FormControl fullWidth>
                                <TextField
                                    label="Full Name"
                                    id="full-name"
                                    value={fullName}
                                    size="small"
                                    onChange={handleFullNameChange}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={12} justifyContent="left" >
                            <FormControl fullWidth>
                                <TextField
                                    label="Email"
                                    id="email"
                                    value={email}
                                    size="small"
                                    onChange={handleEmailChange}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={12} justifyContent="left" >
                            <FormControl fullWidth>
                                <TextField
                                    label="Job title"
                                    id="job-title"
                                    value={jobTitle}
                                    size="small"
                                    onChange={handleJobTitleChange}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6} justifyContent="center" >
                            <Tooltip title="Save" >
                                <IconButton onClick={onSaveAttendee}  color="info">
                                    <SaveIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} md={6} justifyContent="center" >
                            <Tooltip title="Cancel" >
                                <IconButton onClick={onCancelSaveAttendee} color="info">
                                    <CancelIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                }
                    {discoveryCallAttendeesRecord.map(record => (
                        <Grid container spacing={1} justifyContent={'left'}>
                            <Grid item xs={12} md={12} justifyContent="left">
                                <Tooltip title="Delete Attendee" >
                                    <IconButton aria-label="delete" onClick={() => onDeleteAttendee(record.id)}>
                                        <DeleteForeverIcon />
                                    </IconButton>
                                </Tooltip>
                                <Typography variant="caption" >
                                    {record.fullName}
                                    {record.jobTitle != "" &&
                                        <span> - {record.jobTitle}</span>
                                    }
                                </Typography>
                            </Grid>
                            
                        </Grid>
                    ))
                    }
            </CardContent>
        </Card>
    }
    else {
        return <Box>
            <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <Card >
                    <Skeleton variant="text" sx={{ fontSize: 16, ml: 3, mr: 3 }} width={200} />
                </Card>
            </Grid>           
            </Box>
       
    }
}

export { DiscoveryCallAttendees }

export default DiscoveryCallAttendees;